/**
 * The one source for SkipTheDealerships.ai's package names, prices, definitions and core
 * messages. Pages, Hannah's scripts, emails and SEO descriptions read from here, so an edit made
 * once reaches every surface. Nothing in this file is a price or claim that the product cannot
 * currently deliver; see the status notes on each entry.
 *
 * Pure data and string helpers, so both the browser and the server can import it.
 */

/** The business promise. Use it, unchanged, wherever the platform makes its promise. */
export const BUSINESS_PROMISE = 'Verified customers. Vetted vehicles. Qualified buyer matching.';

/** The customer journey, in the order a buyer experiences it. */
export const CUSTOMER_JOURNEY = 'Look. Like. Buy.';

/**
 * The defined terms. Copy must use these meanings and never a looser one. "Verified" means the
 * named check is complete for that person or vehicle, not that the platform verifies everyone.
 */
export const DEFINITIONS = {
  customerVerification: 'Identity has been verified.',
  vehicleVetting: 'Required vehicle checks and disclosures have been completed.',
  preQualifiedCashBuyer: 'Available funds have been verified for the intended purchase.',
  preApprovedFinancingBuyer:
    'Bank approval has been received, subject to its recorded conditions. An application alone is not approval.',
  matchedBuyer:
    'A qualified buyer whose purchasing capacity and vehicle requirements fit the seller’s vehicle.',
};

/** Listing options. Benefits are the established ones; none includes buyer matching. */
export const LISTING_OPTIONS = [
  { id: 'manual', name: 'Manual', price: '$0' },
  { id: 'ai-listing', name: 'AI Listing', price: '$99.99' },
  { id: 'market-value', name: 'Market Value', price: '$149.99' },
];

/**
 * The buyer matching service. Separate from the listing options: it is not included in them, and
 * the listing options are not included in it. The price and commission are the owner's latest
 * direction. Billing frequency, commission caps and timelines are not specified, so none is stated.
 *
 * Status: not yet purchasable. There is no billing product for it, and BillingPage states that
 * paid listing plans are not on sale. Copy describes the service; it does not offer a checkout.
 */
export const MATCHING_PACKAGE = {
  id: 'matching',
  name: 'Buyer matching',
  price: '$299',
  commission: '3%',
  customerCopy:
    '$299 + 3% commission. We match your vehicle with buyers who have verified cash funds or bank-approved financing.',
};

/**
 * Customer status labels for the matching journey. A label must reflect the record that backs
 * it. "Buyer matched" is shown only once a match exists.
 */
export const MATCHING_STATUS = {
  verificationInProgress: 'Verification in progress',
  findingBuyer: 'Finding your buyer',
  buyerMatched: 'Buyer matched',
};

/** Words that must not appear in customer copy: they promise what the platform does not guarantee. */
export const BANNED_PHRASES = [
  'guaranteed sale',
  'guaranteed buyer',
  '100% kyc',
  'kyc-verified buyers and sellers',
  'every user is verified',
  'within 60 seconds',
  'pre-approved buyers notified',
];

/** @returns {Array<string>} banned phrases found in the text (case-insensitive). */
export const findBannedPhrases = text => {
  const lower = String(text || '').toLowerCase();
  return BANNED_PHRASES.filter(phrase => lower.includes(phrase));
};
